import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  ProjectAnalysisReport,
  FileAnalysisReport,
  ProjectSummary
} from '../../../core/models/analysis-report';
import { AnalysisScoringService } from './analysis-scoring.service';
import * as pdfMake from 'pdfmake/build/pdfmake';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';

const htmlToPdfmake = require('html-to-pdfmake') as any;

@Injectable({ providedIn: 'root' })
export class AnalysisExportService {

  private readonly apiUrl: string;

  constructor(
    private http: HttpClient,
    private scoringService: AnalysisScoringService
  ) {
    this.apiUrl = environment.refactorApiUrl;
  }

  // ─── PDF Export ───────────────────────────────────────────────

  async exportToPdf(report: ProjectAnalysisReport): Promise<void> {
    const pdfFonts = await import('pdfmake/build/vfs_fonts');
    const pdf = Object.assign({}, pdfMake, { vfs: (pdfFonts as any).vfs });

    const htmlContent = this.buildReportHtml(report);
    const pdfContent = htmlToPdfmake(htmlContent);

    const docDefinition = {
      content: pdfContent,
      pageSize: 'A4' as const,
      pageOrientation: 'landscape' as const,
      pageMargins: [30, 50, 30, 50] as [number, number, number, number],
      defaultStyle: { fontSize: 9, lineHeight: 1.2, color: '#333' },
      styles: {
        'html-h1': { fontSize: 18, bold: true, color: '#232d4b', margin: [0, 0, 0, 10] as [number, number, number, number] },
        'html-h2': { fontSize: 14, bold: true, color: '#232d4b', margin: [0, 15, 0, 8] as [number, number, number, number] },
        'html-h3': { fontSize: 12, bold: true, color: '#232d4b', margin: [0, 10, 0, 5] as [number, number, number, number] },
        'html-th': { bold: true, fillColor: '#f5f5f5', color: '#232d4b' },
      }
    };

    const filename = this.buildFilename(report.projectName, 'pdf');
    pdf.createPdf(docDefinition).download(filename);
  }

  // ─── HTML Export (from backend) ───────────────────────────────

  async exportToHtml(taskId: string): Promise<void> {
    const url = `${this.apiUrl}/analysis/tasks/${taskId}/report/html`;
    const blob = await firstValueFrom(
      this.http.get(url, { responseType: 'blob' })
    );
    saveAs(blob, `analysis-report-${taskId}.html`);
  }

  // ─── Excel Export ─────────────────────────────────────────────

  async exportToExcel(report: ProjectAnalysisReport): Promise<void> {
    const workbook = XLSX.utils.book_new();

    // Sheet 1: Summary
    const summaryData = this.buildSummarySheet(report);
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    summarySheet['!cols'] = [
      { wch: 28 }, { wch: 12 }, { wch: 10 },
      { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }
    ];
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Sheet 2: Issues detail
    const issuesData = this.buildIssuesSheet(report);
    const issuesSheet = XLSX.utils.aoa_to_sheet(issuesData);
    issuesSheet['!cols'] = [
      { wch: 20 }, { wch: 8 }, { wch: 12 },
      { wch: 14 }, { wch: 8 }, { wch: 50 }, { wch: 40 }
    ];
    XLSX.utils.book_append_sheet(workbook, issuesSheet, 'Issues');

    const filename = this.buildFilename(report.projectName, 'xlsx');
    XLSX.writeFile(workbook, filename);
  }

  // ─── HTML builder (used by PDF) ──────────────────────────────

  private buildReportHtml(report: ProjectAnalysisReport): string {
    let html = '';
    const passed = this.scoringService.qualityGatePassed(report);

    html += `<h1>Analysis Report: ${this.escapeHtml(report.projectName)}</h1>`;
    html += `<p><strong>Quality Gate:</strong> ${passed ? 'PASSED' : 'FAILED'}</p>`;
    html += `<p>${report.totalFilesAnalyzed} files analyzed</p>`;

    // Score summary table
    html += `<h2>Scores</h2>`;
    html += `<table border="1" cellpadding="6" cellspacing="0">`;
    html += `<tr><th>Category</th><th>Score</th><th>Rating</th>`
          + `<th>Critical</th><th>Major/High</th><th>Minor/Medium</th><th>Info</th></tr>`;

    const s = report.summary;
    html += this.buildScoreRow('Clean Code', this.scoringService.cleanCodeScore(s),
      s.criticalViolations, s.majorViolations, s.minorViolations, s.infoViolations);
    html += this.buildScoreRow('Code Health', this.scoringService.codeSmellsScore(s),
      s.criticalSmells, s.majorSmells, s.minorSmells, s.infoSmells);
    html += this.buildScoreRow('Security', this.scoringService.securityScore(s),
      s.criticalVulnerabilities, s.highVulnerabilities,
      s.mediumVulnerabilities, s.infoVulnerabilities);
    html += `</table>`;

    // Per-file issues
    for (const fileReport of report.fileReports) {
      html += `<h2>${this.escapeHtml(fileReport.fileName)}</h2>`;
      if (fileReport.languageDetection) {
        html += `<p>${this.escapeHtml(fileReport.languageDetection.language)} `
              + `${this.escapeHtml(fileReport.languageDetection.version || '')}</p>`;
      }
      html += this.buildIssuesTableHtml(fileReport);
    }

    return html;
  }

  private buildScoreRow(category: string, score: number,
                         critical: number, majorHigh: number,
                         minorMedium: number, info: number): string {
    return `<tr><td>${category}</td><td>${this.scoringService.formatScore(score)}/10</td>`
         + `<td>${this.scoringService.getRating(score)}</td>`
         + `<td>${critical}</td><td>${majorHigh}</td><td>${minorMedium}</td><td>${info}</td></tr>`;
  }

  private buildIssuesTableHtml(fileReport: FileAnalysisReport): string {
    const rows: string[] = [];

    if (fileReport.cleanCodeAnalysis?.violations) {
      for (const v of fileReport.cleanCodeAnalysis.violations) {
        rows.push(this.buildIssueRowHtml(v.id, v.severity, 'Violation',
          v.line, v.description, v.suggestion));
      }
    }
    if (fileReport.codeSmellDetection?.smells) {
      for (const s of fileReport.codeSmellDetection.smells) {
        rows.push(this.buildIssueRowHtml(s.id, s.severity, 'Code Smell',
          s.line, s.description, s.suggestion));
      }
    }
    if (fileReport.securityScan?.vulnerabilities) {
      for (const v of fileReport.securityScan.vulnerabilities) {
        rows.push(this.buildIssueRowHtml(v.id, v.severity, 'Vulnerability',
          v.line, v.description, v.remediation));
      }
    }

    if (rows.length === 0) {
      return '<p><em>No issues found.</em></p>';
    }

    return `<table border="1" cellpadding="4" cellspacing="0">`
         + `<tr><th>ID</th><th>Severity</th><th>Type</th><th>Line</th>`
         + `<th>Description</th><th>Suggestion</th></tr>`
         + rows.join('')
         + `</table>`;
  }

  private buildIssueRowHtml(id: string, severity: string, type: string,
                             line: number | null, description: string,
                             suggestion: string): string {
    return `<tr><td>${this.escapeHtml(id || '')}</td>`
         + `<td>${this.escapeHtml(severity || '')}</td>`
         + `<td>${type}</td>`
         + `<td>${line ?? '-'}</td>`
         + `<td>${this.escapeHtml(description || '')}</td>`
         + `<td>${this.escapeHtml(suggestion || '')}</td></tr>`;
  }

  // ─── Excel sheet builders ─────────────────────────────────────

  private buildSummarySheet(report: ProjectAnalysisReport): any[][] {
    const s = report.summary;
    const cleanCode = this.scoringService.cleanCodeScore(s);
    const codeSmells = this.scoringService.codeSmellsScore(s);
    const security = this.scoringService.securityScore(s);
    return [
      ['Analysis Report', report.projectName],
      ['Files analyzed', report.totalFilesAnalyzed],
      ['Quality Gate', this.scoringService.qualityGatePassed(report) ? 'PASSED' : 'FAILED'],
      [],
      ['Category', 'Score', 'Rating', 'Critical', 'Major/High', 'Minor/Medium', 'Info'],
      ['Clean Code', this.scoringService.formatScore(cleanCode), this.scoringService.getRating(cleanCode),
        s.criticalViolations, s.majorViolations, s.minorViolations, s.infoViolations],
      ['Code Health', this.scoringService.formatScore(codeSmells), this.scoringService.getRating(codeSmells),
        s.criticalSmells, s.majorSmells, s.minorSmells, s.infoSmells],
      ['Security', this.scoringService.formatScore(security), this.scoringService.getRating(security),
        s.criticalVulnerabilities, s.highVulnerabilities,
        s.mediumVulnerabilities, s.infoVulnerabilities],
    ];
  }

  private buildIssuesSheet(report: ProjectAnalysisReport): any[][] {
    const rows: any[][] = [
      ['File', 'ID', 'Severity', 'Type', 'Line', 'Description', 'Suggestion']
    ];

    for (const fileReport of report.fileReports) {
      if (fileReport.cleanCodeAnalysis?.violations) {
        for (const v of fileReport.cleanCodeAnalysis.violations) {
          rows.push([fileReport.fileName, v.id, v.severity, 'Violation',
            v.line ?? '-', v.description, v.suggestion]);
        }
      }
      if (fileReport.codeSmellDetection?.smells) {
        for (const s of fileReport.codeSmellDetection.smells) {
          rows.push([fileReport.fileName, s.id, s.severity, 'Code Smell',
            s.line ?? '-', s.description, s.suggestion]);
        }
      }
      if (fileReport.securityScan?.vulnerabilities) {
        for (const v of fileReport.securityScan.vulnerabilities) {
          rows.push([fileReport.fileName, v.id, v.severity, 'Vulnerability',
            v.line ?? '-', v.description, v.remediation]);
        }
      }
    }

    return rows;
  }

  // ─── Utilities ────────────────────────────────────────────────

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private buildFilename(projectName: string, extension: string): string {
    const date = new Date().toISOString().split('T')[0];
    const sanitized = projectName.replace(/[^a-zA-Z0-9-]/g, '_').toLowerCase();
    return `analysis-report_${sanitized}_${date}.${extension}`;
  }
}
